/*
 * OC Recruitment — For Sales Candidates
 * Design: "The Boardroom" Corporate Modernism
 * Targets packaging sales professionals
 */

import { Link } from "wouter";
import {
  ArrowRight,
  Lock,
  Network,
  TrendingUp,
  Eye,
  Handshake,
  MapPin,
} from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";
import Breadcrumbs from "@/components/Breadcrumbs";
import { pageSEO, organizationSchema } from "@/lib/seoConfig";

const candidatesFAQs = [
  {
    question: "Is there a cost for candidates to work with OC Recruitment?",
    answer: "No. There is never a fee for candidates. Our services are paid for by the packaging companies we work with. You receive career guidance, access to exclusive opportunities, and a confidential job search process at no cost.",
  },
  {
    question: "Will my current employer find out I'm looking for a new role?",
    answer: "Absolutely not. Confidentiality is fundamental to how we operate. We never share your resume or details with any company without your explicit consent. Your current employer will not be contacted or informed at any stage.",
  },
  {
    question: "What kind of packaging sales roles do you typically have available?",
    answer: "We work on roles across the full spectrum of commercial positions within packaging — from Business Development Managers and Account Managers through to Sales Directors, Vice Presidents of Sales, and Chief Commercial Officers. Our roles span flexible packaging, rigid plastics, corrugated, labels, glass, metal, and caps and closures.",
  },
  {
    question: "I'm not actively looking right now. Should I still register?",
    answer: "Yes. Many of the strongest opportunities we work on are time-sensitive and exclusive. By registering with us, you will be the first to hear about relevant roles when they arise. There is no obligation, and your details remain confidential until you decide to explore a specific opportunity.",
  },
  {
    question: "What happens after I submit my resume?",
    answer: "Chris will review your resume and reach out for an initial confidential conversation to understand your career goals, experience, and what you are looking for in your next role. From there, we will keep you informed of relevant opportunities as they arise within your target packaging segments and geographies.",
  },
  {
    question: "Do you only recruit for roles in the packaging industry?",
    answer: "Yes. OC Recruitment focuses exclusively on sales and commercial leadership roles within the packaging industry. This specialist focus means we have a deeper understanding of the market, the companies, and the career paths available to you than any generalist recruiter.",
  },
];

const CANDIDATES_HERO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/hero-candidates-new_73309475.jpg";

const benefits = [
  {
    title: "Confidentiality and Discretion",
    description:
      "Your job search remains completely confidential. We never share your details with any company without your explicit consent, and we understand the sensitivity of exploring new opportunities while currently employed.",
    icon: Lock,
  },
  {
    title: "Access to Hidden Opportunities",
    description:
      "Many of the roles we work on are never advertised publicly. By registering with OC Recruitment, you gain access to exclusive opportunities with leading packaging companies across the United States.",
    icon: Eye,
  },
  {
    title: "Industry-Specific Guidance",
    description:
      "We provide informed career advice grounded in real knowledge of the packaging market. We can tell you which companies are growing, which segments are expanding, and where the strongest opportunities lie.",
    icon: TrendingUp,
  },
  {
    title: "A Genuine Network",
    description:
      "With over sixteen years in packaging recruitment, our network spans the full breadth of the US packaging industry — from flexible packaging and rigid plastics to corrugated packaging, labels & sleeves, glass, metal, and caps & closures.",
    icon: Network,
  },
  {
    title: "Respectful Process",
    description:
      "We treat every candidate as a professional, not a number. You will receive honest feedback, clear timelines, and a recruitment experience that respects your time and your career.",
    icon: Handshake,
  },
  {
    title: "Nationwide Coverage",
    description:
      "We place sales professionals across the entire United States. Whether you are based on the East Coast, West Coast, or anywhere in between, we have opportunities that match your location and ambitions.",
    icon: MapPin,
  },
];

const roleTypes = [
  "Business Development Managers",
  "Sales Representatives",
  "Account Managers",
  "Key Account Managers",
  "Sales Directors",
  "Vice Presidents of Sales",
  "Senior Vice Presidents of Sales",
  "Executive Vice Presidents of Sales",
  "Chief Commercial Officers",
];

const sectorsCovered = [
  "Flexible Packaging",
  "Rigid Plastics",
  "Corrugated Packaging",
  "Folding Carton",
  "Labels & Sleeves",
  "Glass Packaging",
  "Metal Packaging",
  "Caps & Closures",
];

export default function ForCandidates() {
  return (
    <div>
      <SEO
        title={pageSEO.forCandidates.title}
        description={pageSEO.forCandidates.description}
        keywords={pageSEO.forCandidates.keywords}
        canonical={pageSEO.forCandidates.canonical}
        structuredData={organizationSchema}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={CANDIDATES_HERO}
            alt="Packaging sales jobs and careers in the US - submit your resume to OC Recruitment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137]/90 via-[#0F2137]/80 to-[#0F2137]/60" />
        </div>
        <div className="container relative z-10 pt-32 pb-20">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "For Candidates" },
            ]}
            className="mb-6"
          />
          <div className="max-w-2xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                For Sales Candidates
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                Your Next Opportunity in Packaging Sales Starts Here
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                If you are a sales professional working in the packaging
                industry — or looking to move into it — OC Recruitment
                connects you with opportunities that match your experience,
                ambitions, and expertise.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl mb-14">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Why Work With Us
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
                A Search Partner Who Understands Your Industry
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-slate-brand leading-relaxed">
                Working with a specialist recruiter means working with someone
                who understands the nuances of your career. We know what makes
                a strong packaging sales professional, and we know which
                companies offer the environments where you can thrive. With over{" "}
                <Link href="/about" className="text-steel hover:underline">16 years of packaging recruitment experience</Link>,
                we provide career guidance grounded in real market knowledge across{" "}
                <Link href="/packaging-sectors" className="text-steel hover:underline">all major packaging sectors</Link>.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <ScrollReveal key={benefit.title} delay={i * 0.08}>
                <div className="group p-6 rounded border border-border hover:border-steel/30 hover:shadow-md transition-all duration-300 h-full">
                  <div className="w-11 h-11 rounded bg-midnight/5 flex items-center justify-center mb-5 group-hover:bg-steel/10 transition-colors">
                    <benefit.icon size={20} className="text-steel" />
                  </div>
                  <h3 className="text-lg text-midnight mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-slate-brand leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Roles & Sectors */}
      <section className="py-20 lg:py-28 bg-pale">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <ScrollReveal>
                <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                  Roles We Handle
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <h2 className="text-3xl text-midnight mb-8">
                  Types of Positions
                </h2>
              </ScrollReveal>
              <div className="space-y-3">
                {roleTypes.map((role, i) => (
                  <ScrollReveal key={role} delay={i * 0.05}>
                    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-steel flex-shrink-0" />
                      <span className="text-midnight font-medium text-sm">
                        {role}
                      </span>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>

            <div>
              <ScrollReveal>
                <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                  Sectors Covered
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <h2 className="text-3xl text-midnight mb-8">
                  Packaging Segments
                </h2>
              </ScrollReveal>
              <div className="space-y-3">
                {sectorsCovered.map((sector, i) => (
                  <ScrollReveal key={sector} delay={i * 0.05}>
                    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-steel flex-shrink-0" />
                      <span className="text-midnight font-medium text-sm">
                        {sector}
                      </span>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="For Sales Candidates"
        items={candidatesFAQs}
        bgClass="bg-white"
      />

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-midnight text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-white mb-6">
                Submit Your Resume
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-white/70 leading-relaxed mb-10">
                Take the first step towards your next packaging sales
                opportunity. Submit your resume for a confidential conversation
                with Chris about your career goals and the current market.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all duration-200 group"
              >
                Submit Your Resume
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
